import { useState } from 'react';
import { X } from 'lucide-react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { selectSessionAdvisor } from '@client/model/selectors';
import { AdvisorIntegrationsRedtailAuth } from './AdvisorIntegrationsRedtailAuth';
import { AdvisorIntegrationsRedtailContacts } from './AdvisorIntegrationsRedtailContacts';
import { redtailApi } from '@client/api/integrations';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import RedtailLogo from '../images/redtail_logo.svg';

function IntegrationsList() {
  const advisor = useSelector(selectSessionAdvisor);

  const integrations = [
    {
      id: 'redtail',
      name: 'Redtail CRM',
      description: 'Import contacts from Redtail CRM and sync client data',
      logo: RedtailLogo,
      connectUrl: '/integrations/redtail/auth',
      manageUrl: '/integrations/redtail/contacts',
    },
  ];

  return (
    <div className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
      <DashboardPageHeader
        title="Integrations"
        description="Connect your favorite tools to streamline your workflow"
      />

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {integrations.map(integration => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            advisorId={advisor.advisorId}
          />
        ))}
      </div>
    </div>
  );
}

export function AdvisorIntegrations() {
  return (
    <Routes>
      <Route
        path="/"
        element={<IntegrationsList />}
      />
      <Route
        path="/redtail/auth"
        element={<AdvisorIntegrationsRedtailAuth />}
      />
      <Route
        path="/redtail/contacts"
        element={<AdvisorIntegrationsRedtailContacts />}
      />
    </Routes>
  );
}

interface Integration {
  id: string;
  name: string;
  description: string;
  logo: string;
  connectUrl: string;
  manageUrl: string;
}

function IntegrationCard({
  integration,
  advisorId,
}: {
  integration: Integration;
  advisorId: string;
}) {
  const navigate = useNavigate();
  const queryClientHook = useQueryClient();
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  const { data: statusData, isLoading } = useQuery({
    queryKey: ['redtail', 'status'],
    queryFn: () => redtailApi.auth.status(),
    enabled: integration.id === 'redtail',
  });

  const isConnected = statusData?.isActive ?? false;

  const deactivateMutation = useMutation({
    mutationFn: () => redtailApi.auth.deactivate(),
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: ['redtail', 'status'] });
      setShowDeactivateDialog(false);
    },
    onError: (err: Error) => {
      console.error('Failed to deactivate:', err);
      alert(err.message || 'Failed to deactivate integration');
    },
  });

  const handleViewClick = () => {
    navigate(`/advisors/${advisorId}${integration.manageUrl}`);
  };

  const handleConnectClick = () => {
    navigate(`/advisors/${advisorId}${integration.connectUrl}`);
  };

  const handleDeactivateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeactivateDialog(true);
  };

  const handleConfirmDeactivate = () => {
    if (integration.id === 'redtail') {
      deactivateMutation.mutate();
    }
  };

  return (
    <>
      <div className="hover:border-primary-300 flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center justify-center rounded-lg">
            <img
              src={integration.logo}
              alt={integration.name}
              className="h-16 object-contain"
            />
          </div>
          {isLoading ? (
            <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
              Checking...
            </span>
          ) : isConnected ? (
            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-800">
              Connected
            </span>
          ) : null}
        </div>

        <h3 className="mb-2 text-lg font-semibold text-gray-900">{integration.name}</h3>
        <p className="mb-4 flex-1 text-sm text-gray-700">{integration.description}</p>

        {isLoading ? (
          <div className="h-10 animate-pulse rounded-lg bg-gray-200" />
        ) : isConnected ? (
          <div className="flex gap-2">
            <button
              onClick={handleViewClick}
              className="bg-primary hover:bg-primary-600 flex-1 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            >
              View Contacts
            </button>
            <button
              onClick={handleDeactivateClick}
              className="flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-2 text-red-600 transition-colors hover:bg-red-50"
              title="Deactivate integration"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={handleConnectClick}
            className="bg-primary hover:bg-primary-600 w-full rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
          >
            Connect
          </button>
        )}
      </div>

      {/* Deactivation Confirmation Dialog */}
      <AlertDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {integration.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate your connection to {integration.name}. Your imported data will be
              preserved, but you won't be able to sync new data until you reconnect.
              <br />
              <br />
              You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deactivateMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeactivate}
              disabled={deactivateMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
